import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import classNames from 'classnames';
import { Alert, Button, Card, Collapse, Form, Input, InputNumber, Modal, Popconfirm, Radio, Select, Table, Tag, message } from 'antd';
import { CloseOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { LanguageSelectorCMX } from '../../components/formItems';
import ExpandableAlert from '../../components/errors/ExpandableAlert';
import { ColoredTag, ViewPageActions } from '../../updated/pages/GenericCreateEdit/GenericElements';
import { CREATE_OBJECTIVE_CRITERION_MUTATION } from '../../gql/mutations/CREATE_OBJECTIVE_CRITERION_MUTATION';
import { PATCH_OBJECTIVE_CRITERION_MUTATION } from '../../gql/mutations/PATCH_OBJECTIVE_CRITERION_MUTATION';
import { useDeleteObjectiveCriteriaMutation } from '../../gql/hooks';
import { DEFAULT_LOCALE_CODE } from '../../const';
import { useMinimalCourseQuery } from '../../gql/hooks/queries/useMinimalCoursesQuery';
import { ItemTable } from '../../updated/components/SelectorTable/SelectorTable';

const CRITERIA_TYPE = {
    COURSE_COMPLETED_FIXED: 'COURSE_COMPLETED_FIXED',
    COURSE_COMPLETED_COUNT: 'COURSE_COMPLETED_COUNT',
    LOGIN_COUNT: 'LOGIN_COUNT',
};

/** MINIMUM_SEARCH_QUERY's non-filtering variables, which it requires on every call. */
const COURSE_SEARCH = {
    types: [ 'courses' ],
    query: '',
    page: 1,
    sort: 'name',
    order: 'asc',
    localeCode: 'en_US',
};

// shorter than friendlyNames - the Details column carries the count, so the tag only names the kind
const TYPE_TAG_LABELS = {
    [ CRITERIA_TYPE.COURSE_COMPLETED_FIXED ]: 'Complete Specific Course',
    [ CRITERIA_TYPE.COURSE_COMPLETED_COUNT ]: 'Complete Any Courses',
    [ CRITERIA_TYPE.LOGIN_COUNT ]: 'Login',
};

const friendlyNames = {
    [ CRITERIA_TYPE.COURSE_COMPLETED_FIXED ]: 'Complete a specific course',
    [ CRITERIA_TYPE.COURSE_COMPLETED_COUNT ]: 'Complete a number of courses',
    [ CRITERIA_TYPE.LOGIN_COUNT ]: 'Login a number of times',
};

/**
 * A criterion as the objective's criteria list holds it. Three sources feed this and each names
 * things differently: the objective query nests the type as `criterionType.id`, the criterion
 * mutations return a flat `criterionTypeId`, and the search query nests it as
 * `criterionType.criterionTypeId` with the translations under `name` rather than `locales`.
 */
export function formatCriteriaItem( item ) {
    return {
        key: item.id ?? item.criterionId,
        id: item.id ?? item.criterionId,
        targetCount: item.targetCount,
        targetValue: item.targetValue,
        weight: item.weight,
        isEnabled: item.isEnabled,
        criterionType: item.criterionType?.id ?? item.criterionType?.criterionTypeId ??
            item.criterionType ?? item.criterionTypeId,
        locales: item.locales ??
            ( Array.isArray( item.name ) ? item.name.map( n => ( { code: n.locale, name: n.content } ) ) : [] ),
    };
}

/** Enabled criteria first, then lightest weight first. */
export function sortCriteria( list ) {
    return [ ...list ]
        .sort( ( a, b ) => a.weight - b.weight )
        .sort( ( a, b ) => b.isEnabled - a.isEnabled );
}

function getCriteriaName( item, defaultLocaleCode ) {
    const locale = item.locales?.find( l => l.code === defaultLocaleCode ) ?? item.locales?.[ 0 ];
    return locale?.name ?? '';
}

function getCriteriaTypeKey( item, criteriaTypes ) {
    return criteriaTypes.find( t => t.id === item.criterionType )?.name;
}

/**
 * What the Details column says about a criterion - the part of it the type tag leaves out.
 * @param {Object} courseNames course id -> name, for the one type that points at a course
 * @param {boolean} coursesLoading those names are still on their way, so an id missing from them is
 *  unknown rather than gone
 */
function getCriteriaDetails( item, criteriaTypes, courseNames, coursesLoading ) {
    switch ( getCriteriaTypeKey( item, criteriaTypes ) ) {
        case CRITERIA_TYPE.COURSE_COMPLETED_FIXED:
            return courseNames[ item.targetValue ] ??
                ( coursesLoading ? undefined : <Tag color='red'>Course Deleted</Tag> );
        case CRITERIA_TYPE.COURSE_COMPLETED_COUNT:
            return `${ item.targetCount } courses`;
        case CRITERIA_TYPE.LOGIN_COUNT:
            return `${ item.targetCount } logins`;
        default:
            return undefined;
    }
}

/**
 * Criteria localizations live on the criterion as `locales: [ { code, name, description } ]`, which is
 * also the mutation's input shape, so the form holds that list directly. The default locale is pinned
 * to row 0 - the rest are the optional extra translations behind the collapse.
 */
function formatCriteriaForm( criteria, defaultLocaleCode ) {
    // a criterion that has never been translated into a locale comes back with null name and description
    const locales = ( criteria?.locales ?? [] ).filter( l => l.name !== null || l.description !== null );
    const defaultLocale = locales.find( l => l.code === defaultLocaleCode ) ?? { code: defaultLocaleCode };

    return {
        locales: [ defaultLocale, ...locales.filter( l => l !== defaultLocale ) ],
        isEnabled: criteria?.isEnabled ?? true,
        weight: criteria?.weight ?? 0,
        criterionTypeId: criteria?.criterionType ?? criteria?.criterionTypeId,
        targetCount: criteria?.targetCount ?? 1,
        targetValue: criteria?.targetValue,
    };
}

/**
 * Create/edit a single objective criterion. Controlled by the caller: `open` and `onClose` drive
 * visibility, and `onSaved`/`onDeleted` hand back the mutation result so the list can be updated.
 *
 * @param {Object} props.criteria the criterion being edited; omit it to create a new one
 * @param {Array} props.criteriaTypes objectiveCriterionTypes, as `{ id, name }`
 * @param {string} props.disableDeleteReason shown under the footer to explain a blocked delete
 */
export const CriteriaModal = ( { open, criteria, criteriaTypes = [], objectiveId, disabled, disabledIds,
    disableDelete, disableDeleteReason, defaultLocaleCode = DEFAULT_LOCALE_CODE, onSaved, onDeleted, onClose } ) => {

    const [ form ] = Form.useForm();

    const [ createCriteria ] = useMutation( CREATE_OBJECTIVE_CRITERION_MUTATION, { fetchPolicy: 'network-only' } );
    const [ patchCriteria ] = useMutation( PATCH_OBJECTIVE_CRITERION_MUTATION, { fetchPolicy: 'network-only' } );
    const [ deleteCriteria ] = useDeleteObjectiveCriteriaMutation();

    const [ excludedLocales, setExcludedLocales ] = useState( [] );
    const [ loading, setLoading ] = useState( false );
    const [ error, setError ] = useState();

    const isCreating = !criteria?.id;

    const initialValues = useMemo( () => formatCriteriaForm( criteria, defaultLocaleCode ), [ criteria, defaultLocaleCode ] );

    useEffect( () => {
        if ( open ) {
            form.setFieldsValue( initialValues );
            setExcludedLocales( initialValues.locales.map( l => l?.code ) );
        }
    }, [ open, initialValues ] );

    const criterionTypeId = Form.useWatch( 'criterionTypeId', form );
    const selectedType = useMemo( () => criteriaTypes.find( t => t.id === criterionTypeId ), [ criteriaTypes, criterionTypeId ] );

    /**
     * LOGIN_COUNT is not offered for new criteria, but an objective that already has one has to stay
     * editable, so it is only filtered out when it is not the current selection.
     */
    const typeOptions = useMemo( () => criteriaTypes
        .filter( t => t.name !== CRITERIA_TYPE.LOGIN_COUNT || t.id === criterionTypeId )
        .map( t => ( { label: friendlyNames[ t.name ] ?? t.name, value: t.id } ) ),
    [ criteriaTypes, criterionTypeId ] );

    function close() {
        setError();
        setLoading( false );
        form.resetFields();
        onClose?.();
    }

    function handleCancelClick() {
        close();
    }

    function onLocaleChange() {
        setExcludedLocales( ( form.getFieldValue( 'locales' ) ?? [] ).map( l => l?.code ) );
    }

    async function handleDeleteClick() {
        if ( disableDelete ) {
            return;
        }

        setError();
        setLoading( true );

        try {
            await deleteCriteria( { variables: { input: { id: criteria.id } } } );
            message.success( 'Criteria deleted' );
            onDeleted?.( criteria );
            close();
        } catch ( e ) {
            console.error( e );
            setError( <ExpandableAlert message='Could not delete criteria' error={ e } /> );
            setLoading( false );
        }
    }

    async function handleOkClick() {
        setError();
        setLoading( true );

        await form.validateFields()
            .then( async vals => {

                const type = criteriaTypes.find( t => t.id === vals.criterionTypeId );

                const input = {
                    objectiveId,
                    criterionTypeId: vals.criterionTypeId,
                    isEnabled: vals.isEnabled,
                    weight: vals.weight,
                    locales: ( vals.locales ?? [] )
                        .filter( l => l?.code && l?.name )
                        .map( l => ( {
                            code: l.code,
                            name: l.name,
                            description: '',
                        } ) ),
                };

                if ( !isCreating ) {
                    input.id = criteria.id;
                }

                if ( type?.name === CRITERIA_TYPE.COURSE_COMPLETED_FIXED ) {
                    input.targetValue = vals.targetValue;
                    input.targetCount = 1;
                } else {
                    input.targetCount = vals.targetCount;
                }

                try {
                    const result = isCreating ?
                        await createCriteria( { variables: { input } } ) :
                        await patchCriteria( { variables: { input } } );

                    if ( result?.error || result?.errors?.length > 0 ) {
                        throw result.error ?? result.errors;
                    }

                    message.success( 'Criteria saved' );
                    onSaved?.( result.data.Result );
                    close();
                } catch ( e ) {
                    console.error( e );
                    const reason = get( e, 'graphQLErrors[0].extensions.response.body.error.errors[0].message' );
                    setError( reason === 'Target value already used in a criterion within this objective.' ?
                        <ExpandableAlert message='This course is already used in this objective. Please select a different course.' /> :
                        <ExpandableAlert message='Could not save criteria' error={ e } /> );
                    setLoading( false );
                }

            } ).catch( e => {
                console.error( e );
                setError( <ExpandableAlert message='Could not save criteria' error={ e } /> );
                setLoading( false );
            } );
    }

    return (
        <Modal
            title={ isCreating ? 'Add Criteria' : 'Edit Criteria' }
            closeIcon={ null }
            destroyOnHidden={ true }
            open={ open }
            width={ {
                xs: '95vw',
                lg: '900px'
            } }
            footer={ [
                ( isCreating ? <></> :
                    <Popconfirm
                        key='delete'
                        title="Are you sure you want to delete this criteria?"
                        onConfirm={ handleDeleteClick }
                        okText="Yes"
                        cancelText="No">
                        <Button danger className='delete' disabled={ loading || disableDelete } icon={ <DeleteOutlined /> }>
                            Delete
                        </Button>
                    </Popconfirm> ),
                <Button key='cancel' onClick={ handleCancelClick } icon={ <CloseOutlined /> } disabled={ loading }>
                    Cancel
                </Button>,
                <Button key='save' color='primary' type='primary' onClick={ handleOkClick } loading={ loading } >
                    Save
                </Button>
            ] }
            maskClosable={ false }
        >
            <Form form={ form } layout='vertical' initialValues={ initialValues } >
                <Form.Item required label="Locale" name={ [ 'locales', 0, 'code' ] }
                    rules={ [
                        {
                            required: true,
                            message: 'Localization must have locale'
                        }
                    ] }>
                    <LanguageSelectorCMX
                        mode="select-single"
                        disabled={ true }
                    />
                </Form.Item>

                <Form.Item required label="Name" name={ [ 'locales', 0, 'name' ] }
                    rules={ [
                        {
                            required: true,
                            message: 'Localization must have name'
                        },
                        {
                            max: 50,
                            message: 'Name must be less than 50 characters long.'
                        }
                    ] }>
                    <Input
                        disabled={ loading }
                        autoComplete='off'
                        placeholder="Name" />
                </Form.Item>

                <Collapse
                    disabled={ loading }
                    items={ [ {
                        key: '1',
                        label: 'Additional translations',
                        children: <>
                            <Form.List
                                name={ [ 'locales' ] }>
                                { ( fields, { add } ) => (
                                    <>
                                        { fields.filter( ( subfield ) => subfield.key !== 0 ).map( ( subfield ) => {
                                            const locale = form.getFieldValue( [ 'locales', subfield.name, 'code' ] );
                                            return <Card
                                                key={ subfield.key }
                                                size='small'
                                                disabled={ loading }
                                            >
                                                <Form.Item required label="Locale" name={ [ subfield.name, 'code' ] }
                                                    rules={ [
                                                        {
                                                            required: true,
                                                            message: 'Localization must have locale'
                                                        }
                                                    ] }>
                                                    <LanguageSelectorCMX
                                                        mode="select-single"
                                                        disabled={ loading }
                                                        excludelocales={ excludedLocales.filter( l => l !== locale ) }
                                                        hideExcludedLocales={ false }
                                                        onChange={ () => onLocaleChange() }
                                                    />
                                                </Form.Item>

                                                <Form.Item required label="Name" name={ [ subfield.name, 'name' ] }
                                                    rules={ [
                                                        {
                                                            required: true,
                                                            message: 'Localization must have name'
                                                        },
                                                        {
                                                            max: 50,
                                                            message: 'Name must be less than 50 characters long.'
                                                        }
                                                    ] }>
                                                    <Input
                                                        disabled={ loading }
                                                        autoComplete='off'
                                                        placeholder="Name" />
                                                </Form.Item>
                                            </Card>;
                                        }
                                        ) }
                                        <Button
                                            type="dashed"
                                            icon={ <PlusOutlined /> }
                                            disabled={ loading }
                                            onClick={ () => { onLocaleChange(); add(); } }
                                            style={ { width: '100%' } }
                                        >
                                            Add Localization
                                        </Button>
                                    </>
                                ) }
                            </Form.List>
                        </>
                    } ] }
                />

                <Form.Item name="isEnabled" label="Status">
                    <Radio.Group disabled={ loading || disabled }>
                        <Radio.Button value={ false }>Disabled</Radio.Button>
                        <Radio.Button value={ true }>Enabled</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                <Form.Item name="weight" label="Weight">
                    <InputNumber disabled={ loading || disabled } />
                </Form.Item>

                { /* the type decides what the target fields mean, and the saved target is only valid
                    for the type it was picked under, so it is settled at creation and read-only after */ }
                <Form.Item
                    name="criterionTypeId"
                    label="Criteria Type"
                    extra={ isCreating ? undefined : 'A criteria\'s type can not be changed after it is created' }
                    rules={ [
                        {
                            required: true,
                            message: 'A criteria type is required.',
                        }
                    ] }
                >
                    <Select
                        style={ { width: '100%' } }
                        placeholder="Select type..."
                        disabled={ loading || disabled || !isCreating }
                        options={ typeOptions }
                    />
                </Form.Item>

                { selectedType?.name === CRITERIA_TYPE.LOGIN_COUNT &&
                    <Form.Item
                        name="targetCount"
                        label="Required number of logins"
                        rules={ [
                            {
                                required: true,
                                message: 'Enter a number of one or more.',
                            }
                        ] }
                    >
                        <InputNumber min={ 1 } disabled={ loading || disabled } />
                    </Form.Item>
                }

                { selectedType?.name === CRITERIA_TYPE.COURSE_COMPLETED_COUNT &&
                    <Form.Item
                        name="targetCount"
                        label="Number of courses needed to be completed"
                        rules={ [
                            {
                                required: true,
                                message: 'Enter a number of one or more.',
                            },
                            {
                                max: 200,
                                type: 'number',
                                message: 'Number of courses needs to be 200 or less.',
                            }
                        ] }
                    >
                        <InputNumber min={ 1 } disabled={ loading || disabled } />
                    </Form.Item>
                }

                { selectedType?.name === CRITERIA_TYPE.COURSE_COMPLETED_FIXED &&
                    <Form.Item
                        name="targetValue"
                        label="Completion Course"
                        help={ <Alert style={ { marginTop: 10 } } message='Only one course may be chosen per criterion.' type='info' /> }
                        rules={ [
                            {
                                required: true,
                                message: 'A course is required.',
                            }
                        ] }
                    >
                        <CoursePickerFormItem disabledIds={ disabledIds } disabled={ loading || disabled } />
                    </Form.Item>
                }

                { error && error }
                { disableDelete && !isCreating && disableDeleteReason &&
                    <div style={ { fontStyle: 'italic', color: '#aaa', textAlign: 'end' } }>{ disableDeleteReason }</div> }
            </Form>
        </Modal>
    );
};

/**
 * Single-select course picker over the shared search table. `targetValue` holds one course id, so
 * the table's multi-check API is narrowed here: checking a row replaces the value, unchecking the
 * current one clears the field, and a select-all does nothing.
 *
 * Nothing is fetched here - ItemTable.Searchable does its own searching, and a criterion opened for
 * editing arrives with just an id, which the table names through the lookup it runs on init.
 */
const CoursePickerFormItem = ( { value, onChange, disabled, disabledIds } ) => {

    // the whole record behind the current id, once it has been picked in this session
    const [ picked, setPicked ] = useState();

    const selected = useMemo( () => {
        if ( !value ) {
            return [];
        }
        return [ picked?.id === value ? picked : { id: value } ];
    }, [ value, picked ] );

    function handleCheckedChange( records, areChecked ) {
        // there is only ever one course, so the header's select-all has nothing to say here
        if ( records.length !== 1 ) {
            return;
        }

        const [ record ] = records;

        if ( areChecked ) {
            setPicked( record );
            onChange?.( record.id );
        } else if ( record.id === value ) {
            setPicked();
            onChange?.( undefined );
        }
    }

    // the wrapper is what the pane's control layout and the form's error outline hang off of
    return <div 
        className='selector-table'
    >
        <ItemTable.Searchable
            type='courses'
            title='Courses'
            currentLabel='Chosen'
            disabled={ disabled }
            hydrateIds={ selected.map( i => i.id ) }
            disable
            disabledIds={ disabledIds }
            checkedItems={ selected }
            currentItems={ selected.length > 0 ? selected : undefined }
            onCheckedChange={ handleCheckedChange }
        />
    </div>;
};

/**
 * The objective's criteria, read-only unless an actions column is handed in. The objective form and
 * the objectives list both show this, so it owns the course lookup its Details column needs rather
 * than making each caller supply names.
 *
 * @param {Array} props.criteria criteria in formatCriteriaItem's shape
 * @param {Array} props.criteriaTypes objectiveCriterionTypes, as `{ id, name }`
 * @param {Object} props.actionsColumn an antd column appended on the right, for callers that can edit
 */
export const CriteriaTable = ( { criteria = [], criteriaTypes = [], actionsColumn,
    defaultLocaleCode = DEFAULT_LOCALE_CODE } ) => {

    // only a fixed-course criterion carries a course id, so this is every course the table has to name
    const courseIds = useMemo(
        () => [ ...new Set( criteria.map( c => c.targetValue ).filter( Boolean ) ) ],
        [ criteria ] );

    const { courses, loading: coursesLoading } = useMinimalCourseQuery(
        { ...COURSE_SEARCH, limit: courseIds.length, _ids: courseIds },
        { fetchPolicy: 'cache-first', skip: courseIds.length === 0 }
    );

    const courseNames = useMemo( () => Object.fromEntries( courses.map( c => [ c.courseId, c.name ] ) ), [ courses ] );

    const columns = useMemo( () => [
        {
            key: 'title',
            title: 'Title',
            ellipsis: true,
            render: ( _, item ) => getCriteriaName( item, defaultLocaleCode ),
        },
        {
            key: 'enabled',
            title: 'Enabled',
            dataIndex: 'isEnabled',
            width: '140px',
            render: isEnabled => <ColoredTag.Enabled isEnabled={ isEnabled } />,
        },
        {
            key: 'type',
            title: 'Type',
            width: '230px',
            render: ( _, item ) => {
                const type = getCriteriaTypeKey( item, criteriaTypes );
                return type ?
                    <ColoredTag value={ type }>{ TYPE_TAG_LABELS[ type ] ?? type }</ColoredTag> :
                    <span className='canceled'>&mdash;</span>;
            },
        },
        {
            key: 'details',
            title: 'Details',
            ellipsis: true,
            render: ( _, item ) => getCriteriaDetails( item, criteriaTypes, courseNames, coursesLoading ),
        },
        ...( actionsColumn ? [ actionsColumn ] : [] ),
    ], [ criteriaTypes, courseNames, coursesLoading, defaultLocaleCode, actionsColumn ] );

    return <Table
        size='small'
        bordered
        rowKey='id'
        columns={ columns }
        dataSource={ criteria }
        pagination={ false }
        rowClassName={ item => classNames( { 'disabled-item': !item.isEnabled } ) }
    />;
};

/**
 * Criteria card for the objective form. Each criterion is written by its own mutation as the modal is
 * used, so the objective's own save never sends them - the `criteria` form value just mirrors what is
 * on the server, and feeds the repeatable rules and the "needs at least one" check.
 *
 * @param {Array} props.criteriaTypes objectiveCriterionTypes, as `{ id, name }`
 * @param {boolean} props.disabled published objectives with a revision cannot change criteria
 * @param {boolean} props.disableDelete an objective with a revision cannot drop criteria
 */
export const CriteriaField = ( { objectiveId, criteriaTypes = [], disabled, disableDelete,
    defaultLocaleCode = DEFAULT_LOCALE_CODE } ) => {

    return <Card className='card-inner' title='Criteria'>

        { disabled &&
            <Alert
                description="This field cannot be changed while this objective is published."
                type="warning"
                style={ { marginBottom: '12px' } }
            />
        }

        <Form.Item
            name='criteria'
            rules={ [
                {
                    required: true,
                    type: 'array',
                    min: 1,
                    message: 'An objective needs at least one criteria'
                }
            ] }
        >
            <CriteriaFormItem
                objectiveId={ objectiveId }
                criteriaTypes={ criteriaTypes }
                disabled={ disabled }
                disableDelete={ disableDelete }
                defaultLocaleCode={ defaultLocaleCode }
            />
        </Form.Item>
    </Card>;
};

const CriteriaFormItem = ( { value, onChange, objectiveId, criteriaTypes, disabled, disableDelete,
    defaultLocaleCode } ) => {

    const form = Form.useFormInstance();

    const [ deleteCriteria ] = useDeleteObjectiveCriteriaMutation();

    // the criterion the modal is open on - an empty object while adding a new one, undefined while closed
    const [ editing, setEditing ] = useState();

    const criteria = useMemo( () => value ?? [], [ value ] );

    // An objective has to keep one, and the form's own rule only says so at save time - by which
    // point the criterion would already be off the server.
    const isLastCriteria = criteria.length === 1;

    const deleteBlockedReason = disableDelete ?
        'Criteria can not be deleted while this objective has a revision in progress' :
        isLastCriteria ? 'An objective needs at least one criteria. Add another one before deleting this.' :
            undefined;

    /**
     * Criteria are written to the server as the modal is used, so this value mirrors what is already
     * saved rather than holding a pending edit. It still goes out through onChange - that is what
     * revalidates the field and redraws the table - but the touched flag is dropped right after, so
     * the objective form does not report unsaved changes for work that is already committed.
     */
    function commit( next ) {
        onChange?.( next );
        form.setFields( [ { name: 'criteria', touched: false } ] );
    }

    function addCriteria( item ) {
        commit( sortCriteria( [ ...criteria, formatCriteriaItem( item ) ] ) );
    }

    function saveCriteria( item ) {
        commit( sortCriteria( criteria.map( c => c.id === item.id ? formatCriteriaItem( item ) : c ) ) );
    }

    function removeCriteria( item ) {
        commit( criteria.filter( c => c.id !== item.id ) );
    }

    async function handleDeleteClick( item ) {
        if ( disabled || deleteBlockedReason ) {
            return;
        }

        try {
            await deleteCriteria( { variables: { input: { id: item.id } } } );
            removeCriteria( item );
        } catch ( e ) {
            console.error( e );
            message.error( 'Error deleting criteria!' );
        }
    }

    const actionsColumn = useMemo( () => ( {
        key: 'actions',
        title: 'Actions',
        fixed: 'right',
        align: 'center',
        width: '100px',
        render: ( item ) => <ViewPageActions
            item={ item }
            editDisabled={ disabled }
            handleEditCb={ criterion => setEditing( criterion ) }
            deleteDisabled={ disabled || !!deleteBlockedReason }
            handleDeleteCb={ handleDeleteClick }
        />
    // criteria/onChange are in here because the delete handler closes over them - a column built
    // against an older list would drop whatever was added since
    } ), [ disabled, deleteBlockedReason, criteria, onChange ] );

    return <>
        <CriteriaTable
            criteria={ criteria }
            criteriaTypes={ criteriaTypes }
            defaultLocaleCode={ defaultLocaleCode }
            actionsColumn={ actionsColumn }
        />

        <Button
            type="dashed"
            icon={ <PlusOutlined /> }
            disabled={ disabled }
            onClick={ () => setEditing( {} ) }
            style={ { width: '100%', margin: '20px 0' } }
        >
            Add New Criteria
        </Button>

        <CriteriaModal
            open={ !!editing }
            criteria={ editing?.id ? editing : undefined }
            criteriaTypes={ criteriaTypes }
            objectiveId={ objectiveId }
            disabled={ disabled }
            disableDelete={ !!deleteBlockedReason }
            disableDeleteReason={ deleteBlockedReason }
            defaultLocaleCode={ defaultLocaleCode }
            disabledIds={ criteria.filter( c => c.id !== editing?.id && c.targetValue ).map( c => c.targetValue ) }
            onSaved={ item => editing?.id ? saveCriteria( item ) : addCriteria( item ) }
            onDeleted={ removeCriteria }
            onClose={ () => setEditing() }
        />
    </>;
};
